import { useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { useDocumentMeta } from "../hooks/useDocumentMeta";
import { FinalCta } from "../components/FinalCta";
import { FeaturedPost, PostCard } from "../components/actualites/PostCards";

const DESC = "Articles, guides et analyses sur les frais bancaires PME / ETI, l'optimisation bancaire et les bonnes pratiques financières.";

const CollectionLd = ({ posts }) => (
    <script
        type="application/ld+json"
        data-testid="actualites-jsonld"
        dangerouslySetInnerHTML={{
            __html: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "CollectionPage",
                "@id": "https://www.fodbydmb.com/actualites#collection",
                name: "Actualités FOD by DMB Partners",
                description: DESC,
                url: "https://www.fodbydmb.com/actualites",
                inLanguage: "fr-FR",
                isPartOf: { "@type": "WebSite", "@id": "https://www.fodbydmb.com/#website", name: "FOD by DMB Partners" },
                mainEntity: {
                    "@type": "ItemList",
                    numberOfItems: posts.length,
                    itemListElement: posts.map((p, i) => ({
                        "@type": "ListItem",
                        position: i + 1,
                        url: `https://www.fodbydmb.com/post/${p.slug}`,
                        name: p.title,
                    })),
                },
            }).replace(/</g, "\\u003c"),
        }}
    />
);

const Filters = ({ categories, active, onChange }) => (
    <div role="group" aria-label="Filtrer par thème" className="flex flex-wrap gap-2 mt-2">
        {["Tous", ...categories].map((c) => {
            const on = c === active;
            return (
                <button
                    key={c}
                    type="button"
                    aria-pressed={on}
                    onClick={() => onChange(c)}
                    data-testid={`filter-${c.toLowerCase()}`}
                    className={`min-h-[44px] px-[18px] rounded-full border-[1.5px] text-sm transition-colors ${
                        on ? "border-navy bg-navy text-cream font-semibold" : "border-border-strong bg-white text-navy font-medium hover:border-navy"
                    }`}
                >
                    {c}
                </button>
            );
        })}
    </div>
);

export default function Actualites() {
    useDocumentMeta({ title: "Actualités — FOD by DMB Partners", description: DESC, canonical: "https://www.fodbydmb.com/actualites" });
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [active, setActive] = useState("Tous");

    useEffect(() => {
        api.get("/blog").then(({ data }) => setPosts(data || [])).catch(console.error).finally(() => setLoading(false));
    }, []);

    const categories = useMemo(() => [...new Set(posts.map((p) => p.category).filter(Boolean))], [posts]);
    const shown = active === "Tous" ? posts : posts.filter((p) => p.category === active);
    const featured = shown.reduce((a, p) => (!a || (p.created_at || "") > (a.created_at || "") ? p : a), null);
    const rest = shown.filter((p) => p !== featured);

    return (
        <div data-testid="actualites-page">
            {posts.length > 0 && <CollectionLd posts={posts} />}
            <section className="mx-auto max-w-[1180px] px-4 md:px-8 lg:px-12 pt-10 md:pt-16 lg:pt-20 pb-6 flex flex-col gap-[18px] animate-fade-up">
                <div className="text-[13px] font-semibold uppercase tracking-[0.16em] text-gold-text">Actualités</div>
                <h1 className="m-0 font-bold text-[32px] md:text-5xl lg:text-[58px] leading-[1.12] tracking-[-0.025em] text-navy">Insights &amp; analyses.</h1>
                <p className="m-0 text-[17px] leading-relaxed text-grey-text-soft max-w-[640px]">
                    Décryptages, comparatifs et analyses sur l'optimisation des frais bancaires pour les PME et ETI.
                </p>
                {categories.length > 1 && <Filters categories={categories} active={active} onChange={setActive} />}
            </section>
            <section className="mx-auto max-w-[1180px] px-4 md:px-8 lg:px-12 pt-4 pb-12 md:pb-16 lg:pb-[88px] flex flex-col gap-7">
                {loading ? (
                    <div className="flex justify-center py-20"><Loader2 className="text-navy animate-spin" size={32} /></div>
                ) : !featured ? (
                    <p className="text-center text-grey-text-soft py-20">Aucun article pour le moment.</p>
                ) : (
                    <>
                        <FeaturedPost p={featured} />
                        {rest.length > 0 && (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-[22px]">
                                {rest.map((p) => <PostCard key={p.id} p={p} />)}
                            </div>
                        )}
                    </>
                )}
            </section>
            <FinalCta band title="Passez de la lecture à l'action." text="Estimez en 30 secondes ce que votre entreprise pourrait récupérer." />
        </div>
    );
}
